import { useState, useEffect } from 'react';
import { bisector } from 'd3-array';
import { localPoint } from '@visx/event';
import { LinePath } from '@visx/shape';
import type { ScaleTime, ScaleLinear } from 'd3-scale';
import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';
import { useTheme } from '@simplywallst/ui-core';

import { Wrapper, Now } from './styled';

export type HoverParam = {
  time: number;
  value: number | null;
  fairValue: null;
};

interface Props {
  height: number;
  keys: number[];
  entities: Record<number, number | null>;
  periods: ReturnType<typeof getTimeSeriesPeriodMap>;
  positionX: number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  onClick?: () => void;
  fairValue?: number | null;
  onHover?: (param: HoverParam) => void;
  priceFillColor?: string;
}

const bisectDate = bisector((d) => d).left;

export const NarrativeMarker = ({
  height,
  positionX,
  keys,
  entities,
  periods,
  xScale,
  yScale,
  onClick,
  fairValue,
  onHover,
  priceFillColor,
}: Props) => {
  const theme = useTheme();
  const [index, setIndex] = useState<number>(bisectDate(keys, periods.NOW));
  const nextPeriods = getTimeSeriesPeriodMap(
    new Date(keys[index] ? keys[index] : periods.NOW)
  );
  const x = xScale(nextPeriods.NOW);

  useEffect(() => {
    if (keys.length > 0 && index > keys.length - 1) {
      const lastIndex = keys.length - 1;
      setIndex(lastIndex);
    }
  }, [keys, index]);

  const handleMove = (e) => {
    const point = localPoint(e);

    let x = 0;
    if (point !== null && point.x >= 0) {
      x = point.x;
    } else if (point !== null && point.x < 0) {
      // Fallback if Firefox has a negative point.x
      const rect = e.target.getBoundingClientRect();
      x = e.clientX - rect.x;
    }

    const date = xScale.invert(x);
    const nextIndex = bisectDate(keys, date);
    if (nextIndex !== index && nextIndex <= keys.length - 1) {
      setIndex(nextIndex);
      if (onHover) {
        onHover({
          time: keys[nextIndex],
          value: entities[keys[nextIndex]],
          fairValue: null,
        });
      }
    }
  };

  return (
    <Wrapper>
      <svg
        width="100%"
        height={height}
        transform={`translate(${positionX}, 0)`}
        className="touch-none"
      >
        {keys.length > 0 && (
          <>
            <Now
              y="0"
              x={x}
              width="1"
              height={height}
              fill={theme.color.brand01}
              hover
            />
            {fairValue && (
              <LinePath
                data={keys}
                x={(d) => xScale(d || 0) as number}
                y={() => yScale(fairValue || 0) as number}
                stroke={theme.color.chart03}
                strokeWidth={1}
                strokeLinecap="round"
                fill="none"
              />
            )}

            <>
              <g transform={`translate(${x}, 0)`}>
                <rect
                  x="0"
                  y="0"
                  width="12"
                  height="12"
                  fill="white"
                  clipPath="url(#tooltip-arrow-clipping)"
                  transform="rotate(180, 6, 6) translate(5.5, 0)"
                />
              </g>
              <circle
                transform={`translate(${x + 0.5} ${yScale(
                  entities[keys[index]] || 0
                )})`}
                r="3"
                fill={priceFillColor}
                stroke={theme.color.white}
                strokeWidth="1"
              />
              {fairValue && (
                <circle
                  transform={`translate(${x + 0.5} ${yScale(fairValue || 0)})`}
                  r="3"
                  fill={theme.color.chart03}
                  stroke={theme.color.white}
                  strokeWidth="1"
                />
              )}
            </>

            <rect
              width="100%"
              height="100%"
              fill="transparent"
              onMouseMove={handleMove}
              onTouchMove={handleMove}
              onClick={onClick}
            />
          </>
        )}
      </svg>
    </Wrapper>
  );
};
